import classNames from 'classnames';
import type { MouseEventHandler, ReactNode } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { GlowCard } from '@sorare/core/src/components/card/GlowCard';
import { StrokedCard } from '@sorare/core/src/components/card/StrokedCard';
import type { Color, RGBColor } from '@sorare/core/src/style/types';

const Root = styled(Vertical).attrs({ gap: 1 })`
  position: relative;
  isolation: isolate;
`;
const Card = styled.div`
  position: relative;
  isolation: isolate;
  aspect-ratio: var(--card-aspect-ratio);
  transition: filter 0.3s ease-in-out;
  &:not(.disabled) {
    &:hover {
      filter: brightness(120%);
    }
  }
`;

const CornerBadge = styled.div`
  position: absolute;
  right: 0;
  top: var(--unit);

  &.selected {
    right: -2px;
  }
`;

type Props = {
  children: ReactNode;
  footer: ReactNode;
  cornerBadge?: ReactNode;
  selected?: boolean;
  onClickCard?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  ariaLabel?: string;
  className?: string;
  maskImageSrc: string | null;
  disabled?: boolean;
  glowColor?: RGBColor;
};

export const BasicBenchCard = ({
  children,
  footer,
  cornerBadge,
  selected,
  disabled,
  onClickCard,
  ariaLabel,
  className,
  maskImageSrc,
  glowColor,
}: Props) => {
  const active = !!(selected || disabled);

  const { stroke, glow }: { stroke: Color; glow: Color } = (() => {
    if (disabled) {
      return {
        stroke: 'var(--c-nd-600)',
        glow: 'var(--c-nd-600)',
      };
    }
    return {
      stroke: 'var(--c-blue-400)',
      glow: 'rgba(var(--c-rgb-blue-400), 0.8)',
    };
  })();

  return (
    <Root className={className}>
      <ConditionalWrapper
        Wrapper={ButtonBase}
        wrap={!!onClickCard}
        props={{ onClick: onClickCard, disableDebounce: false }}
      >
        <ConditionalWrapper
          Wrapper={GlowCard}
          wrap={!!glowColor}
          props={{ active: !!glowColor, color: glowColor! }}
        >
          <StrokedCard
            active={active}
            color={stroke}
            withGlow={{
              src: maskImageSrc || '',
              color: glow,
            }}
          >
            <Card
              className={classNames({ disabled })}
              as={!onClickCard ? 'div' : undefined}
              aria-label={ariaLabel}
            >
              {children}
            </Card>
          </StrokedCard>
        </ConditionalWrapper>
      </ConditionalWrapper>
      {footer}
      {!!cornerBadge && (
        <CornerBadge className={classNames({ selected })}>
          {cornerBadge}
        </CornerBadge>
      )}
    </Root>
  );
};
